import { useState, useEffect, useCallback } from 'react';
import { useChatBuilderConfig } from '@components/Chat/hooks/useChatBuilderConfig/useChatBuilderConfig';

/**
 * Requests notification permission from the browser.
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
	if (!('Notification' in window)) {
		return false;
	}

	if (Notification.permission === 'granted') {
		return true;
	}

	if (Notification.permission === 'default') {
		const permission = await Notification.requestPermission();
		return permission === 'granted';
	}

	return false;
};

/**
 * Hook to handle checking and requesting browser notification permission.
 * Combines browser permission state with chat builder config to determine
 * if notifications should be enabled.
 */
export const useNotificationPermission = () => {
	const [hasBrowserPermission, setHasBrowserPermission] = useState<boolean>(false);
	const chatBuilderConfig = useChatBuilderConfig();

	useEffect(() => {
		if ('Notification' in window) {
			setHasBrowserPermission(Notification.permission === 'granted');
		}
	}, []);

	const requestPermission = useCallback(async (): Promise<boolean> => {
		const permission = await requestNotificationPermission();
		setHasBrowserPermission(permission);
		return permission;
	}, []);

	const hasNotificationPermission = hasBrowserPermission && !!chatBuilderConfig;

	return { hasNotificationPermission, requestNotificationPermission: requestPermission };
};

